/**
 * FirmaKart.jsx — Firma Kartları sayfasının (FirmaKartlarPage.jsx) tek satırı (backlog #593)
 *
 * Standlar / Kişi Listesi / Kullanıcılar listelerinin satır düzeni (TumStandlarListPage, PersonelKart, KullaniciKart):
 * solda firma logosu (yoksa baş harfler) + altında #id, ad (Firma Detay'a giden gerçek bağlantı) + kısa ad, ortada
 * Yerli / Yabancı rozeti ile fuardaki roller, fuar modunda salon · stand · dekoratör bilgi kutuları, sağda "Detaylar".
 *   · Satır bütünüyle tıklanmaz (diğer listelerde de değil): logo, ad ve "Detaylar" Firma Detay'a götürür; ad ve logo gerçek
 *     href taşır, Ctrl / orta tık yeni sekmede açar.
 *   · Yasaklı firmanın satırı kırmızı çerçeveli ve adın altında kırmızı "Yasaklı" satırı taşır (stand satırlarıyla aynı).
 *   · Satırda sayaç yok (Deniz, #594): yeni backend verisi gerektiren belge / personel sayıları istenmedi.
 *
 * İki modda çalışır:
 *   variant="fuar"   → rol rozetleri + stand/salon bilgisi (fuara eşlenmiş firmalar)
 *   variant="global" → yalnızca kimlik + Yerli / Yabancı; `/fuar-kartlari?allFirms=true` rol/stand alanlarını null döndürür.
 *
 * WAS (#593 öncesi): Card mb-2 / CardBody py-2 px-3 tek satır, baş harf avatarı (id'den renkli), rol rozetleri, düz soluk metin
 * olarak salon/stand/dekoratör, tüm kart tıklanır + sağda chevron.
 */
import React from "react";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { Ban, Eye, Flag, Globe, MapPin, Store, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FIRMA_ROLE_META, OTHER_ROLE_META } from "../../services/firmaFuarKartService";
import { FirmaLogo } from "./FirmaBilgiKartlari";
import { InfoBox, InfoPill } from "../Projects/StandPills";
import { PersonelAdBaglantisi } from "../Kisi/PersonelKartParcalari";

function RoleBadges({ firma }) {
  const { t } = useTranslation("components");
  if (firma.roles.length === 0 && firma.otherRoles.length === 0) {
    return <span className="badge bg-light text-muted fw-normal">{t("firmaKart.rolAtanmamis")}</span>;
  }
  return (
    <>
      {firma.roles.map((r) => {
        const meta = FIRMA_ROLE_META[r.key] || OTHER_ROLE_META;
        return (
          <span key={r.id} className={`badge bg-${meta.color}-subtle text-${meta.color} fw-medium`}>
            <i className={`bx ${meta.icon} me-1`} />{r.name}
          </span>
        );
      })}
      {firma.otherRoles.map((name) => (
        <span
          key={name}
          className={`badge bg-${OTHER_ROLE_META.color}-subtle text-${OTHER_ROLE_META.color} fw-medium`}
          title={t("firmaKart.anaUcRolunDisindaBir")}
        >
          <i className={`bx ${OTHER_ROLE_META.icon} me-1`} />{name}
        </span>
      ))}
    </>
  );
}

/**
 * @param firma     `getFuarFirmaKartlari` satırı (normalizeKart)
 * @param href      Firma Detay adresi (gerçek href: Ctrl / orta tık yeni sekmede açar)
 * @param onDetay   sol tık: sayfanın kendi gezinmesi
 */
export default function FirmaKart({ firma, variant = "global", href, onDetay }) {
  const { t } = useTranslation("components");
  const isFuar = variant === "fuar";
  const hasPlacement = isFuar && (firma.standCount > 0 || firma.salonName || firma.dekoratorName);
  const git = () => onDetay?.(firma);
  const logoTik = (e) => {
    // Değiştirici tuşlu / sol olmayan tık tarayıcıya kalır (yeni sekme).
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    git();
  };

  return (
    <Card className={`mb-3 ${firma.forbidden ? "border border-danger" : ""}`}>
      <CardBody>
        <Row className="align-items-center g-3">
          <Col md="auto">
            <div className="d-flex flex-column align-items-center gap-1">
              <a href={href} onClick={logoTik} title={t("firmaKart.detaylar")} className="d-block" style={{ textDecoration: "none" }}>
                <FirmaLogo
                  profile={{ firmaName: firma.firmaName, firmaShortName: firma.firmaShortname, firmaLogoUrl: firma.firmaLogoUrl }}
                  size={64}
                />
              </a>
              <span className="text-muted font-monospace" style={{ fontSize: "0.7rem" }}>#{firma.firmaId}</span>
            </div>
          </Col>

          <Col md={4} xl={3} style={{ minWidth: 0 }}>
            <div className="fw-bold fs-5 text-break lh-sm" title={firma.firmaName || undefined}>
              <PersonelAdBaglantisi href={href} onGit={git}>{firma.firmaName || "—"}</PersonelAdBaglantisi>
            </div>
            {firma.firmaShortname && (
              <div className="text-muted small text-truncate" title={firma.firmaShortname}>{firma.firmaShortname}</div>
            )}
            {firma.forbidden && (
              <div className="text-danger small fw-semibold mt-1" title={t("firmaKart.fuarAlaninaGirisiYasakli")}>
                <Ban size={12} className="me-1" />{t("firmaKart.yasakli")}
              </div>
            )}
          </Col>

          {/* Yerli / Yabancı + (fuar modunda) o fuardaki roller */}
          <Col md={3}>
            <div className="d-flex flex-wrap align-items-center gap-1">
              {firma.firmaNation != null && (
                <InfoPill
                  className=""
                  icon={firma.firmaNation === 2 ? Globe : Flag}
                  label={firma.firmaNation === 2 ? t("firmaKart.yabanci") : t("firmaKart.yerli")}
                />
              )}
              {isFuar && <RoleBadges firma={firma} />}
            </div>
          </Col>

          {/* Yerleşim — yalnızca fuar modunda */}
          {isFuar && (
            <Col md={4} xl={3}>
              {hasPlacement ? (
                <div className="d-flex flex-wrap gap-2">
                  {firma.salonName && (
                    <InfoBox icon={MapPin} label={`${t("firmaKart.salon")} ${firma.salonName}`} title={t("firmaKart.salon")} />
                  )}
                  {firma.standCount > 0 && (
                    // `stands` GROUP_CONCAT'ı kesilebilir; doğru sayı HER ZAMAN standCount.
                    <InfoBox
                      icon={Store}
                      label={`${firma.standName || firma.standCode}${firma.standCount > 1 ? ` +${firma.standCount - 1}` : ""}`}
                      title={firma.standNames.join(", ")}
                    />
                  )}
                  {firma.dekoratorName && (
                    <div style={{ minWidth: 0, maxWidth: "100%" }}>
                      <InfoBox
                        icon={Wrench}
                        label={firma.dekoratorName}
                        title={t("firmaKart.dekorator", { dekoratorName: firma.dekoratorName })}
                        truncate
                      />
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">—</span>
              )}
            </Col>
          )}

          <Col md="auto" className="d-flex align-items-center ms-md-auto mt-2 mt-md-0">
            <Button color="info" outline size="sm" onClick={git}>
              <Eye size={14} className="me-1" />{t("firmaKart.detaylar")}
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
